import React from 'react';
import { bool, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import config from '../../config';
import StaticMapPreviewContainer from './StaticMapPreviewContainer';
import { DynamicMap, isMapsLibLoaded } from './GoogleMap';
import css from './Map.css';
import useStaticMapPreview from './useStaticMapPreview';

export const Map = props => {
    const {
        className,
        rootClassName,
        mapRootClassName,
        address = '',
        center,
        obfuscatedCenter,
        zoom = config.maps.fuzzy.enabled ? config.maps.fuzzy.defaultZoomLevel : 15,
        mapsConfig = config.maps,
        forceFuzzy,
        children,
        useStaticMap: useStaticMapProp,
    } = props;

    const { useStaticMap, activate: handleActivateDynamicMap, onKeyDown } = useStaticMapPreview(
        useStaticMapProp
    );

    const classes = classNames(rootClassName || css.root, className);
    const mapClasses = mapRootClassName || css.mapRoot;

    if (mapsConfig.fuzzy.enabled && !obfuscatedCenter) {
        throw new Error(
            'Map: obfuscatedCenter prop is required when config.maps.fuzzy.enabled === true'
        );
    }
    if (!mapsConfig.fuzzy.enabled && !forceFuzzy && !center) {
        throw new Error('Map: center prop is required when config.maps.fuzzy.enabled === false');
    }

    const location = mapsConfig.fuzzy.enabled || forceFuzzy ? obfuscatedCenter : center;
    // For dynamic maps require the maps library to be loaded
    if (!isMapsLibLoaded()) {
        return <div className={classes} />;
    }

    // If static map mode is enabled, show the static image immediately
    if (useStaticMap) {
        return (
            <StaticMapPreviewContainer
                onActivate={handleActivateDynamicMap}
                onKeyDown={onKeyDown}
                ariaLabel="Klicken Sie hier, um die interaktive Karte zu öffnen."
                center={location}
                zoom={zoom}
                address={address}
                mapsConfig={mapsConfig}
                forceFuzzy={forceFuzzy}
            />
        );
    }

    return (
        <DynamicMap
            containerElement={<div className={classes} />}
            mapElement={<div className={mapClasses} />}
            containerClassName={classes}
            mapClassName={mapClasses}
            center={location}
            zoom={zoom}
            address={address}
            mapsConfig={mapsConfig}
            forceFuzzy={forceFuzzy}
        >
            {children}
        </DynamicMap>
    );
};

Map.propTypes = {
    className: string,
    rootClassName: string,
    mapRootClassName: string,
    address: string,
    center: propTypes.latlng,
    obfuscatedCenter: propTypes.latlng,
    zoom: number,
    mapsConfig: object,
    useStaticMap: bool,
    forceFuzzy: bool,
};

export default Map;
